import jsPDF from 'jspdf';

// ── Performance colour (same as StudentPerformanceMap) ────────────────────────
const getPerformanceColor = (score: number | null): string => {
  if (score === null || score === undefined) return '#94a3b8';
  if (score >= 90) return '#5d7ed8';
  if (score >= 70) return '#10b981';
  if (score >= 50) return '#f59e0b';
  if (score >= 30) return '#d45f5f';
  return '#991b1b';
};

const getPerformanceLabel = (score: number | null): string => {
  if (score === null || score === undefined) return 'Not Tested';
  if (score >= 90) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Moderate';
  if (score >= 30) return 'Below Avg';
  return 'Poor';
};

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

// ── Flatten nested prerequisites: A. B. C. → A.1 A.2 → A.1.1 ────────────────
function flattenPrereqs(list: any[], level = 0, parentPrefix = ''): string[] {
  const result: string[] = [];
  list.forEach((p, idx) => {
    const name = typeof p === 'object' ? p?.name : p;
    if (!name) return;

    let prefix: string;
    let indent: string;

    if (level === 0) {
      prefix = String.fromCharCode(65 + idx) + '.'; // A. B. C.
      indent = '';
    } else {
      prefix = parentPrefix + (idx + 1);            // A.1  A.2  B.1
      indent = '    '.repeat(level);
    }

    result.push(indent + prefix + ' ' + name);

    if (typeof p === 'object' && p?.children?.length) {
      result.push(...flattenPrereqs(p.children, level + 1, prefix + '.'));
    }
  });
  return result;
}

// ── Build flat rows from performance data ─────────────────────────────────────
function buildRows(topics: any[]) {
  const rows: any[] = [];
  topics.forEach((topic) => {
    (topic.subtopics || []).forEach((sub: any) => {
      const prereqs = sub.prerequisites?.length
        ? sub.prerequisites
        : topic.topic_prerequisites || [];

      rows.push({
        topic:            topic.topic,
        subtopic:         sub.subtopic || '-',
        prerequisites:    flattenPrereqs(prereqs).join('\n') || '-',
        bestScore:        sub.best_score ?? null,
        worstScore:       sub.worst_score ?? null,
        testsTaken:       sub.tests_taken ?? 0,
        learningStatus:   sub.learning_status || 'Not Started',
        trend:            sub.trend || '-',
        learnedPct:       sub.learned_percentage ?? 0,
      });
    });
  });
  return rows;
}

// ── Wrap text ─────────────────────────────────────────────────────────────────
function wrapText(doc: jsPDF, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  for (const para of String(text).split('\n')) {
    lines.push(...doc.splitTextToSize(para, maxWidth));
  }
  return lines;
}

// ── Draw a small progress bar inline ─────────────────────────────────────────
function drawProgressBar(
  doc: jsPDF,
  x: number, y: number,
  width: number, height: number,
  pct: number,
  fillHex: string,
) {
  // Background
  doc.setFillColor(226, 232, 240);
  doc.roundedRect(x, y, width, height, height / 2, height / 2, 'F');
  // Fill
  if (pct > 0) {
    const [r, g, b] = hexToRgb(fillHex);
    doc.setFillColor(r, g, b);
    const fillW = Math.max((pct / 100) * width, height); // min width = height (round end)
    doc.roundedRect(x, y, fillW, height, height / 2, height / 2, 'F');
  }
}

// ── Main export ───────────────────────────────────────────────────────────────
export const exportPerformanceTableToPdf = ({
  data,
  fileName,
  onProgress,
  onSuccess,
  onError,
}: {
  data: any;                  // single subject performance object
  fileName?: string;
  onProgress?: (msg: string, type: string) => void;
  onSuccess?:  (msg: string, type: string) => void;
  onError?:    (msg: string, type: string) => void;
}) => {
  try {
    onProgress?.('Building PDF...', 'info');

    if (!data?.topics?.length) throw new Error('No performance data available');

    const subject  = data.subject || 'Subject';
    const classNum = data.class   || '';
    const pdfName  = fileName
      || `performance-map-${classNum}-${subject}-${new Date().toISOString().slice(0, 10)}.pdf`;

    const rows = buildRows(data.topics);

    // ── Page constants ────────────────────────────────────────────────────────
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

    const PAGE_W  = 297;
    const PAGE_H  = 210;
    const ML      = 10;
    const MR      = 10;
    const MT      = 22;
    const MB      = 12;
    const USABLE  = PAGE_W - ML - MR; // 277mm

    // Column widths  (total = 277)
    // Topic | Subtopic | Prerequisites | Best | Worst | Tests | Status | Trend | Progress
    const COL_W   = [38, 46, 65, 22, 22, 16, 28, 20, 20];
    const HEADERS = ['Topic', 'Subtopic', 'Prerequisites', 'Best%', 'Worst%', 'Tests', 'Status', 'Trend', 'Progress'];

    const HDR_H   = 8;
    const PAD     = 2.5;
    const LINE_H  = 4.5;
    const FONT_SZ = 6.5;

    const colX: number[] = [];
    let cx = ML;
    COL_W.forEach(w => { colX.push(cx); cx += w; });

    let pageNum = 1;

    // ── Helpers ───────────────────────────────────────────────────────────────
    const drawPageHeader = () => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      doc.text(
        `Performance Map: Class ${classNum} – ${subject}`,
        ML, 10,
      );
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, ML, 16);
    };

    const drawTableHeader = (y: number): number => {
      doc.setFillColor(30, 64, 175);
      doc.rect(ML, y, USABLE, HDR_H, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(FONT_SZ);
      doc.setTextColor(255, 255, 255);
      HEADERS.forEach((h, i) => {
        doc.text(h, colX[i] + COL_W[i] / 2, y + HDR_H / 2 + 1.5, { align: 'center' });
      });
      return y + HDR_H;
    };

    const drawFooter = (pn: number, total: number) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text(
        `Page ${pn} of ${total}`,
        PAGE_W / 2, PAGE_H - 4,
        { align: 'center' },
      );
    };

    const drawRow = (row: any, y: number, rowIdx: number): number => {
      const scoreColor   = getPerformanceColor(row.bestScore);
      const perfLabel    = getPerformanceLabel(row.bestScore);

      // Calculate row height
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(FONT_SZ);

      const textCells = [
        { text: row.topic,          col: 0 },
        { text: row.subtopic,       col: 1 },
        { text: row.prerequisites,  col: 2 },
        { text: row.learningStatus, col: 6 },
      ];

      const maxLines = Math.max(
        ...textCells.map(c => wrapText(doc, c.text, COL_W[c.col] - PAD * 2).length),
        2, // minimum 2 lines for progress bar space
      );
      const rowH = Math.max(12, maxLines * LINE_H + PAD * 2);

      // Row background
      doc.setFillColor(rowIdx % 2 === 0 ? 248 : 255, rowIdx % 2 === 0 ? 250 : 255, rowIdx % 2 === 0 ? 252 : 255);
      doc.rect(ML, y, USABLE, rowH, 'F');

      // ── Topic column (coloured) ───────────────────────────────────────────
      const [tr, tg, tb] = hexToRgb(scoreColor);
      doc.setFillColor(tr, tg, tb);
      doc.rect(colX[0], y, COL_W[0], rowH, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(FONT_SZ);
      doc.setTextColor(255, 255, 255);
      const topicLines = wrapText(doc, row.topic, COL_W[0] - PAD * 2);
      const topicBlockH = topicLines.length * LINE_H;
      const topicStartY = y + (rowH - topicBlockH) / 2 + LINE_H - 1;
      topicLines.forEach((line, li) => {
        doc.text(line, colX[0] + COL_W[0] / 2, topicStartY + li * LINE_H, { align: 'center' });
      });

      // ── Subtopic (col 1) ──────────────────────────────────────────────────
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(FONT_SZ);
      doc.setTextColor(30, 41, 59);
      wrapText(doc, row.subtopic, COL_W[1] - PAD * 2).forEach((line, li) => {
        doc.text(line, colX[1] + PAD, y + PAD + (li + 1) * LINE_H - 1);
      });

      // ── Prerequisites (col 2) ─────────────────────────────────────────────
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(FONT_SZ - 0.5);
      doc.setTextColor(71, 85, 105);
      wrapText(doc, row.prerequisites, COL_W[2] - PAD * 2).forEach((line, li) => {
        doc.text(line, colX[2] + PAD, y + PAD + (li + 1) * LINE_H - 1);
      });

      // ── Best Score (col 3) ────────────────────────────────────────────────
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(FONT_SZ + 0.5);
      const bestText = row.bestScore !== null ? `${row.bestScore}%` : 'N/A';
      const [br, bg2, bb] = hexToRgb(scoreColor);
      doc.setTextColor(br, bg2, bb);
      doc.text(bestText, colX[3] + COL_W[3] / 2, y + rowH / 2 + 1.5, { align: 'center' });

      // ── Worst Score (col 4) ───────────────────────────────────────────────
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(FONT_SZ);
      doc.setTextColor(100, 116, 139);
      const worstText = row.worstScore !== null ? `${row.worstScore}%` : 'N/A';
      doc.text(worstText, colX[4] + COL_W[4] / 2, y + rowH / 2 + 1.5, { align: 'center' });

      // ── Tests Taken (col 5) ───────────────────────────────────────────────
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(FONT_SZ + 1);
      doc.setTextColor(30, 41, 59);
      doc.text(String(row.testsTaken), colX[5] + COL_W[5] / 2, y + rowH / 2 + 1.5, { align: 'center' });

      // ── Status (col 6) ────────────────────────────────────────────────────
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(FONT_SZ - 0.5);
      doc.setTextColor(71, 85, 105);
      wrapText(doc, row.learningStatus, COL_W[6] - PAD * 2).forEach((line, li) => {
        doc.text(line, colX[6] + COL_W[6] / 2, y + PAD + (li + 1) * LINE_H - 1, { align: 'center' });
      });

      // ── Trend (col 7) ─────────────────────────────────────────────────────
      doc.setFontSize(FONT_SZ);
      const trendColor =
        row.trend === 'improving' ? '#10b981'
        : row.trend === 'declining' ? '#ef4444'
        : '#94a3b8';
      const [tdr, tdg, tdb] = hexToRgb(trendColor);
      doc.setTextColor(tdr, tdg, tdb);
      doc.setFont('helvetica', 'bold');
      const trendSymbol =
        row.trend === 'improving' ? '▲ Up'
        : row.trend === 'declining' ? '▼ Down'
        : '— Stable';
      doc.text(trendSymbol, colX[7] + COL_W[7] / 2, y + rowH / 2 + 1.5, { align: 'center' });

      // ── Progress bar (col 8) ──────────────────────────────────────────────
      const barX      = colX[8] + PAD;
      const barW      = COL_W[8] - PAD * 2;
      const barH      = 3;
      const barY      = y + rowH / 2 - barH / 2;
      drawProgressBar(doc, barX, barY, barW, barH, row.learnedPct, scoreColor);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(FONT_SZ - 1);
      doc.setTextColor(100, 116, 139);
      doc.text(`${row.learnedPct}%`, barX + barW / 2, barY + barH + 3, { align: 'center' });

      // ── Row grid lines ────────────────────────────────────────────────────
      doc.setDrawColor(203, 213, 225);
      doc.setLineWidth(0.3);
      doc.line(ML, y + rowH, ML + USABLE, y + rowH);
      colX.forEach((x, i) => {
        if (i > 0) doc.line(x, y, x, y + rowH);
      });

      return y + rowH;
    };

    // ── Build pages ───────────────────────────────────────────────────────────
    drawPageHeader();
    let y = drawTableHeader(MT);

    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.4);
    doc.rect(ML, MT, USABLE, HDR_H, 'S');

    rows.forEach((row, idx) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(FONT_SZ);
      const textCells = [row.topic, row.subtopic, row.prerequisites, row.learningStatus];
      const colIdxs   = [0, 1, 2, 6];
      const maxLines  = Math.max(
        ...textCells.map((t, i) => wrapText(doc, t, COL_W[colIdxs[i]] - PAD * 2).length),
        2,
      );
      const rowH = Math.max(12, maxLines * LINE_H + PAD * 2);

      if (y + rowH > PAGE_H - MB) {
        drawFooter(pageNum, 999);
        doc.addPage();
        pageNum++;
        drawPageHeader();
        y = drawTableHeader(MT);
      }

      y = drawRow(row, y, idx);
    });

    // Patch footers with correct total
    const totalPages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      drawFooter(p, totalPages);
    }

    doc.save(pdfName);
    onSuccess?.('PDF exported successfully!', 'success');
  } catch (err: any) {
    console.error('PDF export error:', err);
    onError?.(`Failed to generate PDF: ${err.message}`, 'error');
  }
};